import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { Button } from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeft, HelpCircle } from "lucide-react";
import {
  useServiceStandardQuery,
  useUpdateServiceStandard,
} from "../api/queries";
import {
  ServiceStandardFormTour,
  useServiceStandardFormTour,
} from "../tour/ServiceStandardFormTour";
import { ServiceStandardForm } from "../ui/ServiceStandardForm";

export function ServiceStandardEditPage() {
  const navigate = useNavigate({ from: "/app/service-standard/$id/edit" });
  const { id } = useParams({ strict: false });
  const tour = useServiceStandardFormTour();

  const standard = useServiceStandardQuery(id ?? "");
  const updateServiceStandard = useUpdateServiceStandard(id ?? "");

  if (standard.isLoading) {
    return (
      <>
        <Header />
        <Main>
          <div className="flex items-center justify-center py-12">
            <p className="text-muted-foreground">ກຳລັງໂຫຼດ...</p>
          </div>
        </Main>
      </>
    );
  }

  if (standard.error || !standard.data) {
    return (
      <>
        <Header />
        <Main>
          <div className="flex flex-col items-center justify-center py-12">
            <p className="text-destructive">ເກີດຂໍ້ຜິດພາດໃນການໂຫຼດຂໍ້ມູນ</p>
            <Button
              variant="outline"
              onClick={() => {
                navigate({ to: "/app/service-standard" });
              }}
              className="mt-4"
            >
              ກັບໄປລາຍການ
            </Button>
          </div>
        </Main>
      </>
    );
  }

  const standardData = standard.data;

  return (
    <>
      <Header />
      <Main>
        <ServiceStandardFormTour
          run={tour.run}
          onCallback={tour.handleJoyrideCallback}
        />
        <div className="space-y-6">
          {/* Header Section */}
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <h1 className="font-bold text-2xl">ແກ້ໄຂມາດຕະຖານບໍລິການ</h1>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-7 rounded-full"
                  onClick={tour.startTour}
                >
                  <HelpCircle className="size-5" />
                </Button>
              </div>
              <p className="mt-1 text-muted-foreground">
                ແກ້ໄຂຂໍ້ມູນ: {standardData.name}
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  navigate({
                    to: "/app/service-standard/$id",
                    params: { id: id ?? "" },
                  });
                }}
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                ກັບ
              </Button>
            </div>
          </div>

          {/* Edit Form Section */}
          <div className="rounded-xl border bg-card p-6">
            <ServiceStandardForm
              initialValues={
                standardData
                  ? {
                      name: standardData.name,
                      description: standardData.description ?? undefined,
                      serviceChannelId: standardData.serviceChannelId,
                      departmentId: standardData.departmentId ?? undefined,
                      positionId: standardData.positionId ?? undefined,
                      serviceStage: standardData.serviceStage,
                      status: standardData.status,
                      items:
                        standardData.items?.map((item) => ({
                          id: item.id,
                          itemType: item.itemType,
                          content: item.content,
                          description: item.description,
                          orderIndex: item.orderIndex,
                        })) ?? [],
                      examples:
                        standardData.examples?.map((ex) => ({
                          id: ex.id,
                          situation: ex.situation,
                          example: ex.example,
                          description: ex.description,
                          orderIndex: ex.orderIndex,
                        })) ?? [],
                      specialCases:
                        standardData.specialCases?.map((sc) => ({
                          id: sc.id,
                          title: sc.title,
                          handling: sc.handling,
                          description: sc.description,
                          orderIndex: sc.orderIndex,
                        })) ?? [],
                    }
                  : undefined
              }
              onSubmit={async (vals) => {
                await updateServiceStandard.mutateAsync({
                  name: vals.name,
                  description: vals.description ?? undefined,
                  serviceChannelId: vals.serviceChannelId,
                  departmentId: vals.departmentId ?? undefined,
                  positionId: vals.positionId ?? undefined,
                  serviceStage: vals.serviceStage,
                  status: vals.status,
                  items: vals.items?.map((item, index) => ({
                    ...item,
                    orderIndex: item.orderIndex ?? index,
                  })),
                  examples: vals.examples?.map((ex, index) => ({
                    ...ex,
                    orderIndex: ex.orderIndex ?? index,
                  })),
                  specialCases: vals.specialCases?.map((sc, index) => ({
                    ...sc,
                    orderIndex: sc.orderIndex ?? index,
                  })),
                });
                navigate({
                  to: "/app/service-standard/$id",
                  params: { id: id ?? "" },
                });
              }}
              submitting={updateServiceStandard.isPending}
            />
          </div>
        </div>
      </Main>
    </>
  );
}
